// Единая модель рейтингов для management-прототипа.
//
// Один рейтинг = выбор по 4 ортогональным осям:
//   A. Единица (unit): студия | франчайзи
//   B. Множество (scope): вся сеть | земля | размер города | франчайзи
//   C. Метрика (metric)
//   D. Время — период | неделя | вчера

import { getSupabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import { listPartnerCoverage } from '@/services/territorialManagerService'
import { formatCurrency, formatNumber } from '@/lib/utils'
import type { MetricPeriod } from '@/lib/utils'
import type { PartnerCoverageRow, Studio } from '@/types'
import { isRatingEligibleStudioStatus, loadEffectiveStudioStatusMap } from '@/lib/studioStatus'

const MANAGEMENT_BRANCH_PARTNER_NUMBER = 1000

export type MetricKey =
  | 'revenue'
  | 'max_daily_revenue'
  | 'clients'
  | 'client_growth'
  | 'sales_conversion'
  | 'active_subscriptions'
  | 'new_subscriptions'
  | 'leads_per_studio'
  | 'cost_per_lead'
  | 'romi'
  | 'average_ticket_new_sales'
  | 'visits_per_studio'
  | 'classic_group_index'
  | 'training_quality'

export type UnitKey = 'studio' | 'franchisee'
export type ScopeKey = 'network' | 'land' | 'size' | 'franchisee'
export type ValueKind = 'currency' | 'count' | 'signed_count' | 'percent' | 'decimal'

export interface BoardRow {
  id: string
  place: number
  place_label: string
  primary_label: string
  secondary_label?: string
  tertiary_label?: string
  detail_label?: string
  metric_value: number
  sort_value?: number
  display_value?: string
  isCurrent?: boolean
}

export interface RatingFilterOption {
  value: string
  label: string
}

export interface ScopedRows {
  network: BoardRow[]
  landOptions: RatingFilterOption[]
  landByKey: Record<string, BoardRow[]>
  sizeOptions: RatingFilterOption[]
  sizeByKey: Record<string, BoardRow[]>
  partnerOptions: RatingFilterOption[]
  partnerByKey: Record<string, BoardRow[]>
}

export interface FranchiseeScopedRows {
  network: BoardRow[]
  landOptions: RatingFilterOption[]
  landByKey: Record<string, BoardRow[]>
}

export interface MetricDataset {
  key: MetricKey
  label: string
  valueKind: ValueKind
  detailColumnLabel?: string
  studio: ScopedRows
  franchisee: FranchiseeScopedRows
}

export type UnifiedRatingsData = Record<MetricKey, MetricDataset>

export interface MetricMeta {
  key: MetricKey
  label: string
  valueKind: ValueKind
  detailColumnLabel?: string
}

export const METRIC_META: MetricMeta[] = [
  { key: 'revenue', label: 'Выручка', valueKind: 'currency' },
  { key: 'max_daily_revenue', label: 'Макс. дневная выручка', valueKind: 'currency', detailColumnLabel: 'Дата' },
  { key: 'clients', label: 'Клиенты', valueKind: 'count' },
  { key: 'client_growth', label: 'Прирост клиентов', valueKind: 'signed_count' },
  { key: 'sales_conversion', label: 'Конверсия в продажу', valueKind: 'percent' },
  { key: 'active_subscriptions', label: 'Активные подписки', valueKind: 'count' },
  { key: 'new_subscriptions', label: 'Новые подписки', valueKind: 'count' },
  { key: 'leads_per_studio', label: 'Лиды на студию', valueKind: 'count' },
  { key: 'cost_per_lead', label: 'Цена лида', valueKind: 'currency' },
  { key: 'romi', label: 'ROMI', valueKind: 'decimal' },
  { key: 'average_ticket_new_sales', label: 'Средний чек по абонементам', valueKind: 'currency' },
  { key: 'visits_per_studio', label: 'Посещения на студию', valueKind: 'count' },
  { key: 'classic_group_index', label: 'Индекс групп классики', valueKind: 'percent', detailColumnLabel: 'Занятий' },
  { key: 'training_quality', label: 'Качество тренировок', valueKind: 'percent' }
]

/**
 * Медаль места. Здесь три семейства цвета — единственное осмысленное их
 * сочетание на экране: золото / серебро / бронза. Всё остальное выделение
 * сведено к ролям (см. SELECTED_CLASS в RatingsBoard).
 *
 * ⚠️ Лестницы `fixed-*`, а не обычные, потому что медаль обязана остаться
 * медалью в тёмной теме. Обычная лестница зеркалится (50↔950 … 500↔500),
 * и пара «золотая заливка + тёмный текст» ломалась дважды: текст уезжал в
 * почти-белый, а заливка на самозеркальном оттенке 500 оставалась ярко-жёлтой
 * — 2,07:1 при 6,97:1 в светлой. `fixed-*` не зеркалятся вовсе, поэтому
 * контраст один и тот же в обеих темах по построению.
 *
 * Заливка плоская: градиент здесь ничего не значил, а перепад
 * `from-amber-300 to-amber-500` (ΔE 27) читался на 8-пиксельном кружке
 * как грязь, а не как объём.
 */
export function placeTone(place: number): string {
  if (place === 1) return 'bg-fixed-amber-300 text-fixed-amber-950 ring-2 ring-fixed-amber-200 shadow-sm'
  if (place === 2) return 'bg-fixed-slate-300 text-fixed-slate-900 ring-2 ring-fixed-slate-200 shadow-sm'
  if (place === 3) return 'bg-fixed-orange-300 text-fixed-orange-950 ring-2 ring-fixed-orange-200 shadow-sm'
  return 'bg-muted text-muted-foreground ring-1 ring-border'
}

/**
 * Подкрас строки призового места. Должен читаться как лёгкая подсветка
 * поверхности, а не как заливка, — и одинаково в обеих темах.
 *
 * ⚠️ Серебряная строка НЕ на `bg-slate-50`, в отличие от соседних.
 * Оттенок 50 у цветных лестниц зеркалится в значение, случайно лежащее
 * рядом со светлотой карточки, поэтому золото и бронза остаются лёгкими
 * (ΔE от карточки 5,2 и 4,0 против 5,8 и 3,8 в светлой теме). У нейтрали
 * зеркало уводит на #020617 — заметно ТЕМНЕЕ карточки #212121, и строка
 * превращается в дыру: ΔE 8,5 против 1,9 в светлой, вчетверо. Тут дело в
 * светлоте, а не в насыщенности, поэтому приглушение цветности в
 * генераторе лестниц этот случай не лечит. `bg-muted` — роль «поверхность
 * на шаг в сторону», она по построению остаётся лёгкой в обеих темах
 * (2,1 и 2,9).
 */
export function rowAccentTone(place: number, isCurrent?: boolean): string {
  if (isCurrent) return 'bg-primary/10 ring-1 ring-inset ring-primary/40'
  if (place === 1) return 'bg-amber-50/70'
  if (place === 2) return 'bg-muted/60'
  if (place === 3) return 'bg-orange-50/60'
  return ''
}

/**
 * Заливка пропорционального бара. Те же три «медальных» семейства, но
 * плоские: текста на баре нет, поэтому зеркалящаяся лестница здесь
 * безопасна, а градиент на полоске высотой 6 px не читался вовсе.
 */
export function barTone(place: number): string {
  if (place === 1) return 'bg-amber-500'
  if (place === 2) return 'bg-slate-400'
  if (place === 3) return 'bg-orange-400'
  return 'bg-secondary/30'
}

export function formatRatingValue(valueKind: ValueKind, value: number): string {
  if (valueKind === 'currency') return formatCurrency(value)
  if (valueKind === 'percent') return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`
  if (valueKind === 'decimal') return value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  if (valueKind === 'signed_count') return value > 0 ? `+${formatNumber(value)}` : formatNumber(value)
  return formatNumber(value)
}

function toFiniteNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && Number.isFinite(Number(value))) return Number(value)
  return 0
}

function formatDisplayDate(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
}

function getPopulationOptionSortMeta(
  label: string,
  thresholdByLabel: Map<string, number>
): { bucket: number; order: number; label: string } {
  if (label === 'Без группы') return { bucket: 2, order: Number.MAX_SAFE_INTEGER, label }
  const threshold = thresholdByLabel.get(label)
  if (typeof threshold === 'number' && Number.isFinite(threshold)) return { bucket: 0, order: threshold, label }
  const numericMatch = label.match(/\d+(?:[.,]\d+)?/)
  if (numericMatch) {
    const parsed = Number(numericMatch[0].replace(',', '.'))
    if (Number.isFinite(parsed)) return { bucket: 0, order: parsed, label }
  }
  return { bucket: 1, order: Number.MAX_SAFE_INTEGER, label }
}

function assignPlaces(rows: Array<Omit<BoardRow, 'place' | 'place_label'>>): BoardRow[] {
  const result: BoardRow[] = []
  let index = 0
  while (index < rows.length) {
    const value = rows[index].sort_value ?? rows[index].metric_value
    const groupStart = index
    let groupEnd = index
    while (groupEnd + 1 < rows.length && (rows[groupEnd + 1].sort_value ?? rows[groupEnd + 1].metric_value) === value) groupEnd += 1
    const startPlace = groupStart + 1
    const endPlace = groupEnd + 1
    const placeLabel = startPlace === endPlace ? String(startPlace) : `${startPlace}-${endPlace}`
    for (let i = groupStart; i <= groupEnd; i += 1) {
      result.push({ ...rows[i], place: startPlace, place_label: placeLabel })
    }
    index = groupEnd + 1
  }
  return result
}

interface StudioMetaData {
  studio_number: string
  city_name: string
  country: string | null
  address: string
  land_group_name: string
  population_group_name: string
  partner_number: number | null
  partner_label: string
}

interface ManagementRatingRow {
  studio_internal_id: number
  studio_number: number
  city_name: string | null
  country: string | null
  land_group_id: number | null
  land_group_name: string | null
  population_group_id: number | null
  population_group_name: string | null
  owner_partner_number: number | null
  owner_persons_fio: string | null
  owner_display_name_range: string | null
  gross_revenue_rub: number
  max_daily_revenue_rub: number
  max_daily_revenue_date: string | null
  new_sales_count: number
  new_subscriptions_count: number
  clients_at_start: number | null
  clients_at_end: number | null
  clients_growth: number | null
  active_subscribers_last: number | null
  leads_count: number
  appointments_count: number
  new_visitors_count: number
  admissions_count: number
  new_sales_amount_rub: number
  new_subscriptions_amount_rub: number
  marketing_expenses_amount_rub: number
  owned_days_in_range: number
  range_days: number
  last_metric_date: string | null
}

interface ManagementRatingDailyRow {
  studio_internal_id: number
  studio_number: number
  metric_date: string
  city_name: string | null
  country: string | null
  land_group_id: number | null
  land_group_name: string | null
  population_group_id: number | null
  population_group_name: string | null
  owner_partner_number: number | null
  owner_persons_fio: string | null
  gross_revenue_rub: number
  new_sales_count: number
  new_subscriptions_count: number
  total_clients_count: number | null
  active_subscribers_count: number | null
  leads_count: number
  appointments_count: number
  new_visitors_count: number
  admissions_count: number
  new_sales_amount_rub: number
  new_subscriptions_amount_rub: number
  marketing_expenses_amount_rub: number
}

interface PartnerMaxDailyRevenueRow {
  scope_kind: 'network' | 'land'
  land_group_name: string | null
  owner_partner_number: number
  max_daily_revenue_rub: number
  max_daily_revenue_date: string | null
}

interface SubscriptionSalesRatingRow {
  studio_internal_id: number
  owner_partner_number: number
  subscription_sales_count: number
}

interface TrainerQualityRatingRow {
  studio_internal_id: number
  owner_partner_number: number
  owner_persons_fio: string | null
  land_group_name: string | null
  total_score: number
  max_score: number
  first_training_at: string | null
  last_training_at: string | null
}

interface ClassicRatingRow {
  studio_internal_id: number
  studio_number: number
  city_name: string | null
  country: string | null
  land_group_id: number | null
  land_group_name: string | null
  population_group_id: number | null
  population_group_name: string | null
  owner_partner_number: number | null
  owner_persons_fio: string | null
  group_classes_count: number
  classic_classes_count: number
  classic_visits_count: number
  average_capacity: number | null
  classic_group_index: number | null
}

export interface LoadUnifiedRatingsParams {
  studios: Studio[]
  partnerLabelMap: Map<number, string>
  period: MetricPeriod
  rangeStart: string
  rangeEnd: string
  clientRangeStart: string
  clientRangeEnd: string
  rangeMode: 'period' | 'week' | 'yesterday'
  selectedWeekId?: number | null
  accessScope?: 'management' | 'published'
}

export interface SelfScopeKeys {
  land?: string
  size?: string
  partner?: string
}

async function fetchManagementRatingRows(
  params: { periodId?: number | null; weekId?: number | null }
): Promise<ManagementRatingRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as never as { rpc: (fn: string, args?: Record<string, unknown>) => Promise<{ data: unknown; error: { message?: string } | null }> }).rpc(
      'get_management_rating_rows',
      {
        p_period_id: params.periodId ?? null,
        p_week_id: params.weekId ?? null,
      }
    )
  )

  if (error) throw new Error(error.message || 'Не удалось загрузить агрегированные рейтинговые строки')

  return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
    studio_internal_id: Number(row.studio_internal_id),
    studio_number: Number(row.studio_number),
    city_name: typeof row.city_name === 'string' ? row.city_name : null,
    country: typeof row.country === 'string' ? row.country : null,
    land_group_id: row.land_group_id == null ? null : Number(row.land_group_id),
    land_group_name: typeof row.land_group_name === 'string' ? row.land_group_name : null,
    population_group_id: row.population_group_id == null ? null : Number(row.population_group_id),
    population_group_name: typeof row.population_group_name === 'string' ? row.population_group_name : null,
    owner_partner_number: row.owner_partner_number == null ? null : Number(row.owner_partner_number),
    owner_persons_fio: typeof row.owner_persons_fio === 'string' ? row.owner_persons_fio : null,
    owner_display_name_range: typeof row.owner_display_name_range === 'string' ? row.owner_display_name_range : null,
    gross_revenue_rub: toFiniteNumber(row.gross_revenue_rub),
    max_daily_revenue_rub: toFiniteNumber(row.max_daily_revenue_rub),
    max_daily_revenue_date: typeof row.max_daily_revenue_date === 'string' ? row.max_daily_revenue_date : null,
    new_sales_count: toFiniteNumber(row.new_sales_count),
    new_subscriptions_count: toFiniteNumber(row.new_subscriptions_count),
    clients_at_start: row.clients_at_start == null ? null : toFiniteNumber(row.clients_at_start),
    clients_at_end: row.clients_at_end == null ? null : toFiniteNumber(row.clients_at_end),
    clients_growth: row.clients_growth == null ? null : toFiniteNumber(row.clients_growth),
    active_subscribers_last: row.active_subscribers_last == null ? null : toFiniteNumber(row.active_subscribers_last),
    leads_count: toFiniteNumber(row.leads_count),
    appointments_count: toFiniteNumber(row.appointments_count),
    new_visitors_count: toFiniteNumber(row.new_visitors_count),
    admissions_count: toFiniteNumber(row.admissions_count),
    new_sales_amount_rub: toFiniteNumber(row.new_sales_amount_rub),
    new_subscriptions_amount_rub: toFiniteNumber(row.new_subscriptions_amount_rub),
    marketing_expenses_amount_rub: toFiniteNumber(row.marketing_expenses_amount_rub),
    owned_days_in_range: toFiniteNumber(row.owned_days_in_range),
    range_days: toFiniteNumber(row.range_days),
    last_metric_date: typeof row.last_metric_date === 'string' ? row.last_metric_date : null,
  }))
}

async function fetchPublishedRatingRows(
  params: { periodId?: number | null; weekId?: number | null }
): Promise<ManagementRatingRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as never as { rpc: (fn: string, args?: Record<string, unknown>) => Promise<{ data: unknown; error: { message?: string } | null }> }).rpc(
      'get_published_rating_rows',
      {
        p_period_id: params.periodId ?? null,
        p_week_id: params.weekId ?? null,
      }
    )
  )

  if (error) throw new Error(error.message || 'Не удалось загрузить опубликованные рейтинговые строки')

  return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
    studio_internal_id: Number(row.studio_internal_id),
    studio_number: Number(row.studio_number),
    city_name: typeof row.city_name === 'string' ? row.city_name : null,
    country: typeof row.country === 'string' ? row.country : null,
    land_group_id: row.land_group_id == null ? null : Number(row.land_group_id),
    land_group_name: typeof row.land_group_name === 'string' ? row.land_group_name : null,
    population_group_id: row.population_group_id == null ? null : Number(row.population_group_id),
    population_group_name: typeof row.population_group_name === 'string' ? row.population_group_name : null,
    owner_partner_number: row.owner_partner_number == null ? null : Number(row.owner_partner_number),
    owner_persons_fio: typeof row.owner_persons_fio === 'string' ? row.owner_persons_fio : null,
    owner_display_name_range: typeof row.owner_display_name_range === 'string' ? row.owner_display_name_range : null,
    gross_revenue_rub: toFiniteNumber(row.gross_revenue_rub),
    max_daily_revenue_rub: toFiniteNumber(row.max_daily_revenue_rub),
    max_daily_revenue_date: typeof row.max_daily_revenue_date === 'string' ? row.max_daily_revenue_date : null,
    new_sales_count: toFiniteNumber(row.new_sales_count),
    new_subscriptions_count: toFiniteNumber(row.new_subscriptions_count),
    clients_at_start: row.clients_at_start == null ? null : toFiniteNumber(row.clients_at_start),
    clients_at_end: row.clients_at_end == null ? null : toFiniteNumber(row.clients_at_end),
    clients_growth: row.clients_growth == null ? null : toFiniteNumber(row.clients_growth),
    active_subscribers_last: row.active_subscribers_last == null ? null : toFiniteNumber(row.active_subscribers_last),
    leads_count: toFiniteNumber(row.leads_count),
    appointments_count: toFiniteNumber(row.appointments_count),
    new_visitors_count: toFiniteNumber(row.new_visitors_count),
    admissions_count: toFiniteNumber(row.admissions_count),
    new_sales_amount_rub: toFiniteNumber(row.new_sales_amount_rub),
    new_subscriptions_amount_rub: toFiniteNumber(row.new_subscriptions_amount_rub),
    marketing_expenses_amount_rub: toFiniteNumber(row.marketing_expenses_amount_rub),
    owned_days_in_range: toFiniteNumber(row.owned_days_in_range),
    range_days: toFiniteNumber(row.range_days),
    last_metric_date: typeof row.last_metric_date === 'string' ? row.last_metric_date : null,
  }))
}

async function fetchManagementRatingDailyRows(
  startDate: string,
  endDate: string
): Promise<ManagementRatingDailyRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as never as { rpc: (fn: string, args?: Record<string, unknown>) => Promise<{ data: unknown; error: { message?: string } | null }> }).rpc(
      'get_management_rating_daily_rows',
      {
        p_start_date: startDate,
        p_end_date: endDate,
      }
    )
  )

  if (error) throw new Error(error.message || 'Не удалось загрузить дневные рейтинговые строки')

  return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
    studio_internal_id: Number(row.studio_internal_id),
    studio_number: Number(row.studio_number),
    metric_date: String(row.metric_date),
    city_name: typeof row.city_name === 'string' ? row.city_name : null,
    country: typeof row.country === 'string' ? row.country : null,
    land_group_id: row.land_group_id == null ? null : Number(row.land_group_id),
    land_group_name: typeof row.land_group_name === 'string' ? row.land_group_name : null,
    population_group_id: row.population_group_id == null ? null : Number(row.population_group_id),
    population_group_name: typeof row.population_group_name === 'string' ? row.population_group_name : null,
    owner_partner_number: row.owner_partner_number == null ? null : Number(row.owner_partner_number),
    owner_persons_fio: typeof row.owner_persons_fio === 'string' ? row.owner_persons_fio : null,
    gross_revenue_rub: toFiniteNumber(row.gross_revenue_rub),
    new_sales_count: toFiniteNumber(row.new_sales_count),
    new_subscriptions_count: toFiniteNumber(row.new_subscriptions_count),
    total_clients_count: row.total_clients_count == null ? null : toFiniteNumber(row.total_clients_count),
    active_subscribers_count: row.active_subscribers_count == null ? null : toFiniteNumber(row.active_subscribers_count),
    leads_count: toFiniteNumber(row.leads_count),
    appointments_count: toFiniteNumber(row.appointments_count),
    new_visitors_count: toFiniteNumber(row.new_visitors_count),
    admissions_count: toFiniteNumber(row.admissions_count),
    new_sales_amount_rub: toFiniteNumber(row.new_sales_amount_rub),
    new_subscriptions_amount_rub: toFiniteNumber(row.new_subscriptions_amount_rub),
    marketing_expenses_amount_rub: toFiniteNumber(row.marketing_expenses_amount_rub),
  }))
}

async function fetchPublishedRatingDailyRows(
  startDate: string,
  endDate: string
): Promise<ManagementRatingDailyRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as never as { rpc: (fn: string, args?: Record<string, unknown>) => Promise<{ data: unknown; error: { message?: string } | null }> }).rpc(
      'get_published_rating_daily_rows',
      {
        p_start_date: startDate,
        p_end_date: endDate,
      }
    )
  )

  if (error) throw new Error(error.message || 'Не удалось загрузить опубликованные дневные рейтинговые строки')

  return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
    studio_internal_id: Number(row.studio_internal_id),
    studio_number: Number(row.studio_number),
    metric_date: String(row.metric_date),
    city_name: typeof row.city_name === 'string' ? row.city_name : null,
    country: typeof row.country === 'string' ? row.country : null,
    land_group_id: row.land_group_id == null ? null : Number(row.land_group_id),
    land_group_name: typeof row.land_group_name === 'string' ? row.land_group_name : null,
    population_group_id: row.population_group_id == null ? null : Number(row.population_group_id),
    population_group_name: typeof row.population_group_name === 'string' ? row.population_group_name : null,
    owner_partner_number: row.owner_partner_number == null ? null : Number(row.owner_partner_number),
    owner_persons_fio: typeof row.owner_persons_fio === 'string' ? row.owner_persons_fio : null,
    gross_revenue_rub: toFiniteNumber(row.gross_revenue_rub),
    new_sales_count: toFiniteNumber(row.new_sales_count),
    new_subscriptions_count: toFiniteNumber(row.new_subscriptions_count),
    total_clients_count: row.total_clients_count == null ? null : toFiniteNumber(row.total_clients_count),
    active_subscribers_count: row.active_subscribers_count == null ? null : toFiniteNumber(row.active_subscribers_count),
    leads_count: toFiniteNumber(row.leads_count),
    appointments_count: toFiniteNumber(row.appointments_count),
    new_visitors_count: toFiniteNumber(row.new_visitors_count),
    admissions_count: toFiniteNumber(row.admissions_count),
    new_sales_amount_rub: toFiniteNumber(row.new_sales_amount_rub),
    new_subscriptions_amount_rub: toFiniteNumber(row.new_subscriptions_amount_rub),
    marketing_expenses_amount_rub: toFiniteNumber(row.marketing_expenses_amount_rub),
  }))
}

async function fetchSubscriptionSalesRatingRows(
  startDate: string,
  endDate: string
): Promise<SubscriptionSalesRatingRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as never as { rpc: (fn: string, args?: Record<string, unknown>) => Promise<{ data: unknown; error: { message?: string } | null }> }).rpc(
      'get_subscription_sales_rating_rows',
      {
        p_start_date: startDate,
        p_end_date: endDate,
      }
    )
  )

  if (error) throw new Error(error.message || 'Не удалось загрузить продажи подписок для рейтинга')

  return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
    studio_internal_id: Number(row.studio_internal_id),
    owner_partner_number: Number(row.owner_partner_number),
    subscription_sales_count: toFiniteNumber(row.subscription_sales_count),
  }))
}


// Пик дневной выручки франчайзи считается в БД: раньше фронт тянул ради него всю
// дневную базу сети (тысячи строк) и упирался в PostgREST max-rows = 1000, отчего
// метрика считалась по обрезку диапазона.
async function fetchPartnerMaxDailyRevenue(
  startDate: string,
  endDate: string,
  functionName: string
): Promise<PartnerMaxDailyRevenueRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as never as { rpc: (fn: string, args?: Record<string, unknown>) => Promise<{ data: unknown; error: { message?: string } | null }> }).rpc(
      functionName,
      {
        p_start_date: startDate,
        p_end_date: endDate,
      }
    )
  )

  if (error) throw new Error(error.message || 'Не удалось загрузить пиковую дневную выручку франчайзи')

  return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
    scope_kind: row.scope_kind === 'land' ? 'land' : 'network',
    land_group_name: typeof row.land_group_name === 'string' ? row.land_group_name : null,
    owner_partner_number: Number(row.owner_partner_number),
    max_daily_revenue_rub: toFiniteNumber(row.max_daily_revenue_rub),
    max_daily_revenue_date: typeof row.max_daily_revenue_date === 'string' ? row.max_daily_revenue_date : null,
  }))
}

async function fetchTrainerQualityRatings(
  startDate: string,
  endDate: string,
  functionName = 'get_trainer_quality_rating_rows'
): Promise<TrainerQualityRatingRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as never as { rpc: (fn: string, args?: Record<string, unknown>) => Promise<{ data: unknown; error: { message?: string } | null }> }).rpc(
      functionName,
      {
        p_start_date: startDate,
        p_end_date: endDate,
      }
    )
  )

  if (error) throw new Error(error.message || 'Не удалось загрузить рейтинг качества тренировок')

  return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
    studio_internal_id: Number(row.studio_internal_id),
    owner_partner_number: Number(row.owner_partner_number),
    owner_persons_fio: typeof row.owner_persons_fio === 'string' ? row.owner_persons_fio : null,
    land_group_name: typeof row.land_group_name === 'string' ? row.land_group_name : null,
    total_score: toFiniteNumber(row.total_score),
    max_score: toFiniteNumber(row.max_score),
    first_training_at: typeof row.first_training_at === 'string' ? row.first_training_at : null,
    last_training_at: typeof row.last_training_at === 'string' ? row.last_training_at : null,
  }))
}

async function fetchClassicRatingRows(
  params: { periodId?: number | null; weekId?: number | null; functionName?: string }
): Promise<ClassicRatingRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as never as { rpc: (fn: string, args?: Record<string, unknown>) => Promise<{ data: unknown; error: { message?: string } | null }> }).rpc(
      params.functionName ?? 'get_classic_rating_rows',
      {
        p_period_id: params.periodId ?? null,
        p_week_id: params.weekId ?? null,
      }
    )
  )

  if (error) throw new Error(error.message || 'Не удалось загрузить рейтинг классики')

  return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
    studio_internal_id: Number(row.studio_internal_id),
    studio_number: Number(row.studio_number),
    city_name: typeof row.city_name === 'string' ? row.city_name : null,
    country: typeof row.country === 'string' ? row.country : null,
    land_group_id: row.land_group_id == null ? null : Number(row.land_group_id),
    land_group_name: typeof row.land_group_name === 'string' ? row.land_group_name : null,
    population_group_id: row.population_group_id == null ? null : Number(row.population_group_id),
    population_group_name: typeof row.population_group_name === 'string' ? row.population_group_name : null,
    owner_partner_number: row.owner_partner_number == null ? null : Number(row.owner_partner_number),
    owner_persons_fio: typeof row.owner_persons_fio === 'string' ? row.owner_persons_fio : null,
    group_classes_count: toFiniteNumber(row.group_classes_count),
    classic_classes_count: toFiniteNumber(row.classic_classes_count),
    classic_visits_count: toFiniteNumber(row.classic_visits_count),
    average_capacity: row.average_capacity == null ? null : toFiniteNumber(row.average_capacity),
    classic_group_index: row.classic_group_index == null ? null : toFiniteNumber(row.classic_group_index),
  }))
}

function normalizeOwnerLabel(
  partnerNumber: number | null | undefined,
  ownerPersonsFio: string | null | undefined,
  partnerLabelMap: Map<number, string>
): string {
  if (partnerNumber == null) return 'Франчайзи не указан'
  if (ownerPersonsFio?.trim()) return `${ownerPersonsFio.trim()} (${partnerNumber})`
  return partnerLabelMap.get(partnerNumber) || `Франчайзи ${partnerNumber}`
}

function formatOwnerRangeLabel(rawLabel: string | null | undefined, currentLabel?: string | null): string {
  const parts = Array.from(
    new Map(
      (rawLabel || '')
        .split(' - ')
        .map((item) => item.trim())
        .filter(Boolean)
        .map((item) => [item, item])
    ).values()
  )
  if (parts.length === 0) return 'Франчайзи не указан'
  if (parts.length === 1) return parts[0]
  const preferredCurrent = currentLabel?.trim()
  const current = (preferredCurrent && parts.find((part) => part === preferredCurrent)) || parts[parts.length - 1]
  const previous = parts.filter((part) => part !== current)
  if (previous.length === 0) return current
  return `${current} (ex. ${previous.join(', ')})`
}

function buildPartnerEquivalentMetric(
  rows: ManagementRatingRow[],
  valueGetter: (row: ManagementRatingRow) => number
): {
  metricByPartner: Map<number, number>
  displayByPartner: Map<number, string>
  sortByPartner: Map<number, number>
} {
  const accumulators = new Map<number, { totalValue: number; totalOwnedStudioDays: number; rangeDays: number }>()

  for (const row of rows) {
    if (row.owner_partner_number == null) continue
    const aggregate = accumulators.get(row.owner_partner_number) || {
      totalValue: 0,
      totalOwnedStudioDays: 0,
      rangeDays: row.range_days || 0,
    }
    aggregate.totalValue += valueGetter(row)
    aggregate.totalOwnedStudioDays += row.owned_days_in_range || 0
    aggregate.rangeDays = row.range_days || aggregate.rangeDays
    accumulators.set(row.owner_partner_number, aggregate)
  }

  const metricByPartner = new Map<number, number>()
  const displayByPartner = new Map<number, string>()
  const sortByPartner = new Map<number, number>()

  for (const [partnerNumber, aggregate] of accumulators.entries()) {
    const equivalentStudios = aggregate.rangeDays > 0
      ? aggregate.totalOwnedStudioDays / aggregate.rangeDays
      : 0
    const value = equivalentStudios > 0 ? aggregate.totalValue / equivalentStudios : 0
    metricByPartner.set(partnerNumber, value)
    sortByPartner.set(partnerNumber, value)
    displayByPartner.set(
      partnerNumber,
      value.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    )
  }

  return { metricByPartner, displayByPartner, sortByPartner }
}

function buildPartnerEquivalentMetricByLand(
  rows: ManagementRatingRow[],
  studioMeta: Map<number, StudioMetaData>,
  valueGetter: (row: ManagementRatingRow) => number
): {
  metricByLand: Record<string, Map<number, number>>
  displayByLand: Record<string, Map<number, string>>
  sortByLand: Record<string, Map<number, number>>
} {
  const accumulators = new Map<string, Map<number, { totalValue: number; totalOwnedStudioDays: number; rangeDays: number }>>()

  for (const row of rows) {
    if (row.owner_partner_number == null) continue
    const landName = studioMeta.get(row.studio_internal_id)?.land_group_name || 'Без земли'
    const byPartner = accumulators.get(landName) || new Map<number, { totalValue: number; totalOwnedStudioDays: number; rangeDays: number }>()
    const aggregate = byPartner.get(row.owner_partner_number) || {
      totalValue: 0,
      totalOwnedStudioDays: 0,
      rangeDays: row.range_days || 0,
    }
    aggregate.totalValue += valueGetter(row)
    aggregate.totalOwnedStudioDays += row.owned_days_in_range || 0
    aggregate.rangeDays = row.range_days || aggregate.rangeDays
    byPartner.set(row.owner_partner_number, aggregate)
    accumulators.set(landName, byPartner)
  }

  const metricByLand: Record<string, Map<number, number>> = {}
  const displayByLand: Record<string, Map<number, string>> = {}
  const sortByLand: Record<string, Map<number, number>> = {}

  for (const [landName, byPartner] of accumulators.entries()) {
    const metricByPartner = new Map<number, number>()
    const displayByPartner = new Map<number, string>()
    const sortByPartner = new Map<number, number>()
    for (const [partnerNumber, aggregate] of byPartner.entries()) {
      const equivalentStudios = aggregate.rangeDays > 0
        ? aggregate.totalOwnedStudioDays / aggregate.rangeDays
        : 0
      const value = equivalentStudios > 0 ? aggregate.totalValue / equivalentStudios : 0
      metricByPartner.set(partnerNumber, value)
      sortByPartner.set(partnerNumber, value)
      displayByPartner.set(
        partnerNumber,
        value.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
      )
    }
    metricByLand[landName] = metricByPartner
    displayByLand[landName] = displayByPartner
    sortByLand[landName] = sortByPartner
  }

  return { metricByLand, displayByLand, sortByLand }
}

export async function loadUnifiedManagementRatings(
  params: LoadUnifiedRatingsParams
): Promise<UnifiedRatingsData> {
  const supabase = getSupabase()
  const accessScope = params.accessScope ?? 'management'
  const [
    rawBaseRows,
    rawDailyRows,
    partnerCoverage,
    populationGroupsData,
    rawClassicRatingRows,
    rawQualityRows,
    rawPartnerMaxDailyRows,
    rawSubscriptionSalesRows,
  ] = await Promise.all([
    params.rangeMode === 'yesterday'
      ? Promise.resolve([] as ManagementRatingRow[])
      : accessScope === 'published'
        ? fetchPublishedRatingRows({
            periodId: params.rangeMode === 'period' ? params.period.period_id : null,
            weekId: params.rangeMode === 'week' ? params.selectedWeekId ?? null : null,
          })
        : fetchManagementRatingRows({
            periodId: params.rangeMode === 'period' ? params.period.period_id : null,
            weekId: params.rangeMode === 'week' ? params.selectedWeekId ?? null : null,
          }),
    // Дневные строки нужны только режиму «за вчера» (там они — сам источник рейтинга,
    // один день = сотни строк). Для периода/недели их место занял серверный агрегат
    // пиковой дневной выручки: диапазон в 28 дней давал тысячи строк и обрезался
    // лимитом PostgREST в 1000.
    params.rangeMode !== 'yesterday'
      ? Promise.resolve([] as ManagementRatingDailyRow[])
      : accessScope === 'published'
        ? fetchPublishedRatingDailyRows(params.rangeStart, params.rangeEnd)
        : fetchManagementRatingDailyRows(params.rangeStart, params.rangeEnd),
    accessScope === 'published'
      ? listPartnerCoverage().catch(() => [] as PartnerCoverageRow[])
      : listPartnerCoverage(),
    withAuthRecovery(() =>
      supabase.from('population_groups').select('population_threshold, description').order('population_threshold', { ascending: true })
    ) as Promise<{ data: Array<{ population_threshold: number; description: string }> | null; error: { message?: string } | null }>,
    params.rangeMode === 'yesterday'
      ? Promise.resolve([] as ClassicRatingRow[])
      : fetchClassicRatingRows({
          periodId: params.rangeMode === 'period' ? params.period.period_id : null,
          weekId: params.rangeMode === 'week' ? params.selectedWeekId ?? null : null,
          functionName: accessScope === 'published' ? 'get_published_classic_rating_rows' : 'get_classic_rating_rows',
        }).catch((error) => {
          console.warn('Unified ratings: classic rating loading failed, classic metric will be empty', error)
          return []
        }),
    fetchTrainerQualityRatings(
      params.rangeStart,
      params.rangeEnd,
      accessScope === 'published' ? 'get_published_trainer_quality_rating_rows' : 'get_trainer_quality_rating_rows'
    ).catch((error) => {
      console.warn('Unified ratings: trainer quality loading failed, quality metric will be empty', error)
      return []
    }),
    fetchPartnerMaxDailyRevenue(
      params.rangeStart,
      params.rangeEnd,
      accessScope === 'published'
        ? 'get_published_partner_max_daily_revenue'
        : 'get_management_partner_max_daily_revenue'
    ).catch((error) => {
      // Если RPC ещё не накатан, гасим только «Макс. дневную выручку» франчайзи,
      // а не весь экран (как с классикой и качеством тренировок).
      console.warn('Unified ratings: partner peak daily revenue loading failed, franchisee metric will be empty', error)
      return [] as PartnerMaxDailyRevenueRow[]
    }),
    fetchSubscriptionSalesRatingRows(params.rangeStart, params.rangeEnd),
  ])

  const baseRows = rawBaseRows.filter((row) => row.owner_partner_number !== MANAGEMENT_BRANCH_PARTNER_NUMBER)
  const dailyRows = rawDailyRows.filter((row) => row.owner_partner_number !== MANAGEMENT_BRANCH_PARTNER_NUMBER)
  const classicRatingRows = rawClassicRatingRows.filter((row) => row.owner_partner_number !== MANAGEMENT_BRANCH_PARTNER_NUMBER)
  const qualityRows = rawQualityRows.filter((row) => row.owner_partner_number !== MANAGEMENT_BRANCH_PARTNER_NUMBER)
  const partnerMaxDailyRows = rawPartnerMaxDailyRows.filter((row) => row.owner_partner_number !== MANAGEMENT_BRANCH_PARTNER_NUMBER)
  const subscriptionSalesRows = rawSubscriptionSalesRows.filter(
    (row) => row.owner_partner_number !== MANAGEMENT_BRANCH_PARTNER_NUMBER
  )

  if (populationGroupsData.error) {
    throw new Error(populationGroupsData.error.message || 'Не удалось загрузить группы населенности')
  }

  const candidateStudioIds = Array.from(new Set([
    ...params.studios.map((studio) => studio.studio_internal_id),
    ...baseRows.map((row) => row.studio_internal_id),
    ...dailyRows.map((row) => row.studio_internal_id),
    ...classicRatingRows.map((row) => row.studio_internal_id),
    ...qualityRows.map((row) => row.studio_internal_id),
  ]))
  const currentStatusByStudioId = new Map(
    params.studios.map((studio) => [studio.studio_internal_id, studio.status_code ?? 'CLOSED'])
  )
  const effectiveStatusByStudioId = await loadEffectiveStudioStatusMap(
    supabase as never as Parameters<typeof loadEffectiveStudioStatusMap>[0],
    candidateStudioIds,
    params.rangeEnd,
    currentStatusByStudioId
  )
  const eligibleStudioIdSet = new Set(
    Array.from(effectiveStatusByStudioId.entries())
      .filter(([, statusCode]) => isRatingEligibleStudioStatus(statusCode))
      .map(([studioId]) => studioId)
  )

  if (eligibleStudioIdSet.size === 0) {
    return buildEmptyDataset()
  }

  const normalizedRows: ManagementRatingRow[] = (params.rangeMode === 'yesterday'
    ? dailyRows.map((row) => ({
        studio_internal_id: row.studio_internal_id,
        studio_number: row.studio_number,
        city_name: row.city_name,
        country: row.country,
        land_group_id: row.land_group_id,
        land_group_name: row.land_group_name,
        population_group_id: row.population_group_id,
        population_group_name: row.population_group_name,
        owner_partner_number: row.owner_partner_number,
        owner_persons_fio: row.owner_persons_fio,
        owner_display_name_range: normalizeOwnerLabel(row.owner_partner_number, row.owner_persons_fio, params.partnerLabelMap),
        gross_revenue_rub: row.gross_revenue_rub,
        max_daily_revenue_rub: row.gross_revenue_rub,
        max_daily_revenue_date: row.metric_date,
        new_sales_count: row.new_sales_count,
        new_subscriptions_count: row.new_subscriptions_count,
        clients_at_start: null,
        clients_at_end: row.total_clients_count,
        clients_growth: null,
        active_subscribers_last: row.active_subscribers_count,
        leads_count: row.leads_count,
        appointments_count: row.appointments_count,
        new_visitors_count: row.new_visitors_count,
        admissions_count: row.admissions_count,
        new_sales_amount_rub: row.new_sales_amount_rub,
        new_subscriptions_amount_rub: row.new_subscriptions_amount_rub,
        marketing_expenses_amount_rub: row.marketing_expenses_amount_rub,
        owned_days_in_range: 1,
        range_days: 1,
        last_metric_date: row.metric_date,
      }))
    : baseRows).filter((row) => eligibleStudioIdSet.has(row.studio_internal_id))

  const coverageByPartner = new Map<number, PartnerCoverageRow>(partnerCoverage.map((row) => [Number(row.partner_number), row]))

  const populationGroupThresholdByLabel = new Map<string, number>(
    (populationGroupsData.data || []).map((row) => [row.description, row.population_threshold])
  )

  const studioMeta = new Map<number, StudioMetaData>(
    params.studios
      .filter((studio) => eligibleStudioIdSet.has(studio.studio_internal_id) && studio.partner_number !== MANAGEMENT_BRANCH_PARTNER_NUMBER)
      .map((studio) => [
      studio.studio_internal_id,
      {
        studio_number: studio.studio_number,
        city_name: studio.city?.city_name || 'Город не указан',
        country: studio.city?.country || null,
        address: studio.address || 'Адрес не указан',
        land_group_name: 'Без земли',
        population_group_name: 'Без группы',
        partner_number: studio.partner_number ?? null,
        partner_label: studio.partner_number != null
          ? (params.partnerLabelMap.get(studio.partner_number) || `Франчайзи ${studio.partner_number}`)
          : 'Франчайзи не указан',
      },
    ])
  )

  const ownerRangeRawLabelByStudio = new Map<number, string>()
  const partnerAtRangeEndByStudio = new Map<number, number | null>()
  const partnerLabelAtRangeEndByStudio = new Map<number, string>()
  const partnerRangeSelectorRankByStudio = new Map<number, number>()

  for (const row of normalizedRows) {
    const current = studioMeta.get(row.studio_internal_id)
    const fallbackPartnerLabel = normalizeOwnerLabel(row.owner_partner_number, row.owner_persons_fio, params.partnerLabelMap)
    studioMeta.set(row.studio_internal_id, {
      studio_number: String(row.studio_number),
      city_name: row.city_name || current?.city_name || 'Город не указан',
      country: row.country || current?.country || null,
      address: current?.address || 'Адрес не указан',
      land_group_name: row.land_group_name || current?.land_group_name || 'Без земли',
      population_group_name: row.population_group_name || current?.population_group_name || 'Без группы',
      partner_number: row.owner_partner_number ?? current?.partner_number ?? null,
      partner_label: current?.partner_label || fallbackPartnerLabel,
    })

    const ownerRangeRawLabel = row.owner_display_name_range || fallbackPartnerLabel
    const previousLabel = ownerRangeRawLabelByStudio.get(row.studio_internal_id)
    if (!previousLabel || ownerRangeRawLabel.length > previousLabel.length) {
      ownerRangeRawLabelByStudio.set(row.studio_internal_id, ownerRangeRawLabel)
    }

    const rank =
      (row.clients_at_end != null ? 1_000_000_000 : 0) +
      (row.active_subscribers_last != null ? 100_000_000 : 0) +
      new Date(row.last_metric_date || row.max_daily_revenue_date || '1970-01-01').getTime()

    if (!partnerRangeSelectorRankByStudio.has(row.studio_internal_id) || rank >= (partnerRangeSelectorRankByStudio.get(row.studio_internal_id) || 0)) {
      partnerRangeSelectorRankByStudio.set(row.studio_internal_id, rank)
      partnerAtRangeEndByStudio.set(row.studio_internal_id, row.owner_partner_number ?? null)
      partnerLabelAtRangeEndByStudio.set(row.studio_internal_id, fallbackPartnerLabel)
    }
  }

  for (const [studioId, meta] of studioMeta.entries()) {
    const rawOwnerLabel = ownerRangeRawLabelByStudio.get(studioId)
    const currentPartnerLabel = partnerLabelAtRangeEndByStudio.get(studioId)
    if (rawOwnerLabel) {
      meta.partner_label = formatOwnerRangeLabel(rawOwnerLabel, currentPartnerLabel || meta.partner_label)
    }
    if (partnerLabelAtRangeEndByStudio.has(studioId)) {
      meta.partner_number = partnerAtRangeEndByStudio.get(studioId) ?? meta.partner_number
    }
  }

  for (const [studioId, meta] of Array.from(studioMeta.entries())) {
    if (meta.partner_number === MANAGEMENT_BRANCH_PARTNER_NUMBER) {
      studioMeta.delete(studioId)
      eligibleStudioIdSet.delete(studioId)
    }
  }

  const partnerLabelFromRangeByPartner = new Map<number, string>()
  const partnerCitiesFromRangeByPartner = new Map<number, Set<string>>()
  const partnerCitiesFromRangeByLand = new Map<string, Map<number, Set<string>>>()
  for (const meta of studioMeta.values()) {
    if (meta.partner_number == null) continue
    if (!partnerLabelFromRangeByPartner.has(meta.partner_number) && meta.partner_label) {
      partnerLabelFromRangeByPartner.set(meta.partner_number, meta.partner_label)
    }
    const cityName = meta.city_name?.trim()
    if (!cityName || cityName === 'Город не указан') continue
    const cities = partnerCitiesFromRangeByPartner.get(meta.partner_number) || new Set<string>()
    cities.add(cityName)
    partnerCitiesFromRangeByPartner.set(meta.partner_number, cities)

    const landName = meta.land_group_name || 'Без земли'
    const citiesByPartner = partnerCitiesFromRangeByLand.get(landName) || new Map<number, Set<string>>()
    const landCities = citiesByPartner.get(meta.partner_number) || new Set<string>()
    landCities.add(cityName)
    citiesByPartner.set(meta.partner_number, landCities)
    partnerCitiesFromRangeByLand.set(landName, citiesByPartner)
  }

  const getPartnerRankingLabel = (partnerNumber: number | null | undefined): string => {
    if (partnerNumber == null) return 'Франчайзи не указан'
    const labelFromRange = partnerLabelFromRangeByPartner.get(Number(partnerNumber))
    if (labelFromRange) return labelFromRange
    const coverage = coverageByPartner.get(Number(partnerNumber))
    if (coverage?.persons_fio) return `${coverage.persons_fio} (${partnerNumber})`
    return params.partnerLabelMap.get(partnerNumber) || `Франчайзи ${partnerNumber}`
  }

  const getPartnerCitiesLabel = (partnerNumber: number, landName?: string): string | null => {
    if (landName) {
      const citiesFromLand = partnerCitiesFromRangeByLand.get(landName)?.get(partnerNumber)
      if (citiesFromLand && citiesFromLand.size > 0) {
        return Array.from(citiesFromLand).sort((a, b) => a.localeCompare(b, 'ru')).join(', ')
      }
    }
    const citiesFromRange = partnerCitiesFromRangeByPartner.get(partnerNumber)
    if (citiesFromRange && citiesFromRange.size > 0) {
      return Array.from(citiesFromRange).sort((a, b) => a.localeCompare(b, 'ru')).join(', ')
    }
    const coverage = coverageByPartner.get(partnerNumber)
    return coverage?.cities_text || null
  }

  const buildStudioRow = (studioId: number, value: number, detail?: string): Omit<BoardRow, 'place' | 'place_label'> => {
    const meta = studioMeta.get(studioId)
    return {
      id: `studio-${studioId}`,
      primary_label: `№${meta?.studio_number || studioId}`,
      secondary_label: meta?.city_name || 'Город не указан',
      tertiary_label: meta?.partner_label || 'Франчайзи не указан',
      detail_label: detail,
      metric_value: value,
    }
  }

  const sortStudioEntries = (
    entries: Array<[number, number]>,
    opts: {
      sortValueByStudio?: Map<number, number>
      secondarySortByStudio?: Map<number, number>
    } = {}
  ): Array<[number, number]> =>
    entries.sort((a, b) => {
      const sortA = opts.sortValueByStudio?.get(a[0]) ?? a[1]
      const sortB = opts.sortValueByStudio?.get(b[0]) ?? b[1]
      if (sortB !== sortA) return sortB - sortA
      const secondaryA = opts.secondarySortByStudio?.get(a[0]) ?? Number.NEGATIVE_INFINITY
      const secondaryB = opts.secondarySortByStudio?.get(b[0]) ?? Number.NEGATIVE_INFINITY
      if (secondaryB !== secondaryA) return secondaryB - secondaryA
      const metaA = studioMeta.get(a[0])
      const metaB = studioMeta.get(b[0])
      return (metaA?.studio_number || '').localeCompare(metaB?.studio_number || '', 'ru', { numeric: true })
    })

  const buildStudioBoardRows = (
    metricByStudio: Map<number, number>,
    opts: {
      allowNonZero?: boolean
      includeAll?: boolean
      detailByStudio?: Map<number, string>
      displayByStudio?: Map<number, string>
      sortValueByStudio?: Map<number, number>
      secondarySortByStudio?: Map<number, number>
      partnerNumberByStudio?: Map<number, number | null>
    } = {}
  ): BoardRow[] => {
    const entries = sortStudioEntries(
      Array.from(metricByStudio.entries()).filter(([, value]) => {
        if (opts.includeAll) return true
        return opts.allowNonZero ? value !== 0 : value > 0
      }),
      {
        sortValueByStudio: opts.sortValueByStudio,
        secondarySortByStudio: opts.secondarySortByStudio,
      }
    )
    return assignPlaces(entries.map(([studioId, value]) => {
      const row = buildStudioRow(studioId, value, opts.detailByStudio?.get(studioId))
      if (opts.displayByStudio?.has(studioId)) row.display_value = opts.displayByStudio.get(studioId)
      if (opts.sortValueByStudio?.has(studioId)) row.sort_value = opts.sortValueByStudio.get(studioId)
      return row
    }))
  }

  const buildScopedRows = (
    metricByStudio: Map<number, number>,
    opts: {
      allowNonZero?: boolean
      includeAll?: boolean
      detailByStudio?: Map<number, string>
      displayByStudio?: Map<number, string>
      sortValueByStudio?: Map<number, number>
      secondarySortByStudio?: Map<number, number>
      partnerNumberByStudio?: Map<number, number | null>
    } = {}
  ): ScopedRows => {
    const filterValue = (value: number) => {
      if (opts.includeAll) return true
      return opts.allowNonZero ? value !== 0 : value > 0
    }

    const network = buildStudioBoardRows(metricByStudio, opts)
    const studioIdsByLand = new Map<string, Array<[number, number]>>()
    const studioIdsBySize = new Map<string, Array<[number, number]>>()
    const studioIdsByPartner = new Map<string, Array<[number, number]>>()

    for (const [studioId, value] of metricByStudio.entries()) {
      if (!filterValue(value)) continue
      const meta = studioMeta.get(studioId)
      if (!meta) continue
      const effectivePartnerNumber = opts.partnerNumberByStudio?.has(studioId)
        ? opts.partnerNumberByStudio.get(studioId) ?? null
        : meta.partner_number
      const partnerKey = effectivePartnerNumber != null ? String(effectivePartnerNumber) : 'none'

      ;(studioIdsByLand.get(meta.land_group_name) || studioIdsByLand.set(meta.land_group_name, []).get(meta.land_group_name)!).push([studioId, value])
      ;(studioIdsBySize.get(meta.population_group_name) || studioIdsBySize.set(meta.population_group_name, []).get(meta.population_group_name)!).push([studioId, value])
      if (partnerKey !== 'none') {
        ;(studioIdsByPartner.get(partnerKey) || studioIdsByPartner.set(partnerKey, []).get(partnerKey)!).push([studioId, value])
      }
    }

    const buildGroupRows = (entries: Array<[number, number]>) => assignPlaces(sortStudioEntries(entries, {
      sortValueByStudio: opts.sortValueByStudio,
      secondarySortByStudio: opts.secondarySortByStudio,
    }).map(([id, v]) => {
      const row = buildStudioRow(id, v, opts.detailByStudio?.get(id))
      if (opts.displayByStudio?.has(id)) row.display_value = opts.displayByStudio.get(id)
      if (opts.sortValueByStudio?.has(id)) row.sort_value = opts.sortValueByStudio.get(id)
      return row
    }))

    const landOptions: RatingFilterOption[] = []
    const landByKey: Record<string, BoardRow[]> = {}
    Array.from(studioIdsByLand.keys())
      .sort((a, b) => (a === 'Без земли' ? 1 : b === 'Без земли' ? -1 : a.localeCompare(b, 'ru')))
      .forEach((name) => {
        landOptions.push({ value: name, label: name })
        landByKey[name] = buildGroupRows(studioIdsByLand.get(name) || [])
      })

    const sizeOptions: RatingFilterOption[] = []
    const sizeByKey: Record<string, BoardRow[]> = {}
    Array.from(studioIdsBySize.keys())
      .sort((a, b) => {
        const metaA = getPopulationOptionSortMeta(a, populationGroupThresholdByLabel)
        const metaB = getPopulationOptionSortMeta(b, populationGroupThresholdByLabel)
        if (metaA.bucket !== metaB.bucket) return metaA.bucket - metaB.bucket
        if (metaA.order !== metaB.order) return metaA.order - metaB.order
        return metaA.label.localeCompare(metaB.label, 'ru', { numeric: true })
      })
      .forEach((name) => {
        sizeOptions.push({ value: name, label: name })
        sizeByKey[name] = buildGroupRows(studioIdsBySize.get(name) || [])
      })

    const partnerOptions: RatingFilterOption[] = []
    const partnerByKey: Record<string, BoardRow[]> = {}
    Array.from(studioIdsByPartner.keys())
      .sort((a, b) => getPartnerRankingLabel(Number(a)).localeCompare(getPartnerRankingLabel(Number(b)), 'ru', { numeric: true }))
      .forEach((key) => {
        partnerOptions.push({ value: key, label: getPartnerRankingLabel(Number(key)) })
        partnerByKey[key] = buildGroupRows(studioIdsByPartner.get(key) || [])
      })

    return { network, landOptions, landByKey, sizeOptions, sizeByKey, partnerOptions, partnerByKey }
  }

  const buildPartnerBoardRows = (
    metricByPartner: Map<number, number>,
    opts: {
      allowNonZero?: boolean
      includeAll?: boolean
      citiesLabelByPartner?: Map<number, string>
      detailByPartner?: Map<number, string>
      displayByPartner?: Map<number, string>
      sortValueByPartner?: Map<number, number>
      secondarySortByPartner?: Map<number, number>
    } = {}
  ): BoardRow[] => {
    const studioCountByPartner = new Map<number, number>()
    for (const meta of studioMeta.values()) {
      if (meta.partner_number == null) continue
      studioCountByPartner.set(meta.partner_number, (studioCountByPartner.get(meta.partner_number) || 0) + 1)
    }

    const entries = Array.from(metricByPartner.entries())
      .filter(([, value]) => {
        if (opts.includeAll) return true
        return opts.allowNonZero ? value !== 0 : value > 0
      })
      .sort((a, b) => {
        const sortA = opts.sortValueByPartner?.get(a[0]) ?? a[1]
        const sortB = opts.sortValueByPartner?.get(b[0]) ?? b[1]
        if (sortB !== sortA) return sortB - sortA
        const secondaryA = opts.secondarySortByPartner?.get(a[0]) ?? Number.NEGATIVE_INFINITY
        const secondaryB = opts.secondarySortByPartner?.get(b[0]) ?? Number.NEGATIVE_INFINITY
        if (secondaryB !== secondaryA) return secondaryB - secondaryA
        return getPartnerRankingLabel(a[0]).localeCompare(getPartnerRankingLabel(b[0]), 'ru', { numeric: true })
      })

    return assignPlaces(entries.map(([partnerNumber, value]) => {
      const coverage = coverageByPartner.get(partnerNumber)
      const row: Omit<BoardRow, 'place' | 'place_label'> = {
        id: `partner-${partnerNumber}`,
        primary_label: getPartnerRankingLabel(partnerNumber),
        secondary_label:
          opts.citiesLabelByPartner?.get(partnerNumber) ||
          getPartnerCitiesLabel(partnerNumber) ||
          `${studioCountByPartner.get(partnerNumber) || 0} студий`,
        detail_label: opts.detailByPartner?.get(partnerNumber),
        metric_value: value,
      }
      if (opts.displayByPartner?.has(partnerNumber)) row.display_value = opts.displayByPartner.get(partnerNumber)
      if (opts.sortValueByPartner?.has(partnerNumber)) row.sort_value = opts.sortValueByPartner.get(partnerNumber)
      return row
    }))
  }

  const buildFranchiseeScopedRows = (
    metricByPartner: Map<number, number>,
    landMetricByPartner: Record<string, Map<number, number>>,
    opts: {
      allowNonZero?: boolean
      includeAll?: boolean
      detailByPartner?: Map<number, string>
      detailByPartnerByLand?: Record<string, Map<number, string>>
      displayByPartner?: Map<number, string>
      displayByPartnerByLand?: Record<string, Map<number, string>>
      sortValueByPartner?: Map<number, number>
      sortValueByPartnerByLand?: Record<string, Map<number, number>>
      secondarySortByPartner?: Map<number, number>
      secondarySortByPartnerByLand?: Record<string, Map<number, number>>
    } = {}
  ): FranchiseeScopedRows => {
    const network = buildPartnerBoardRows(metricByPartner, opts)
    const landOptions: RatingFilterOption[] = []
    const landByKey: Record<string, BoardRow[]> = {}

    Object.keys(landMetricByPartner)
      .sort((a, b) => (a === 'Без земли' ? 1 : b === 'Без земли' ? -1 : a.localeCompare(b, 'ru')))
      .forEach((landName) => {
        landOptions.push({ value: landName, label: landName })
        const citiesLabelByPartner = new Map<number, string>()
        for (const partnerNumber of (landMetricByPartner[landName] || new Map<number, number>()).keys()) {
          const citiesLabel = getPartnerCitiesLabel(partnerNumber, landName)
          if (citiesLabel) citiesLabelByPartner.set(partnerNumber, citiesLabel)
        }
        landByKey[landName] = buildPartnerBoardRows(landMetricByPartner[landName] || new Map<number, number>(), {
          allowNonZero: opts.allowNonZero,
          includeAll: opts.includeAll,
          citiesLabelByPartner,
          detailByPartner: opts.detailByPartnerByLand?.[landName],
          displayByPartner: opts.displayByPartnerByLand?.[landName],
          sortValueByPartner: opts.sortValueByPartnerByLand?.[landName],
          secondarySortByPartner: opts.secondarySortByPartnerByLand?.[landName],
        })
      })

    return { network, landOptions, landByKey }
  }

  const dataset = (
    key: MetricKey,
    studioMetric: Map<number, number>,
    partnerMetric: Map<number, number>,
    landPartnerMetric: Record<string, Map<number, number>>,
    opts: {
      allowNonZero?: boolean
      includeAll?: boolean
      detailByStudio?: Map<number, string>
      detailByPartner?: Map<number, string>
      detailByPartnerByLand?: Record<string, Map<number, string>>
      displayByStudio?: Map<number, string>
      displayByPartner?: Map<number, string>
      displayByPartnerByLand?: Record<string, Map<number, string>>
      sortValueByStudio?: Map<number, number>
      sortValueByPartner?: Map<number, number>
      sortValueByPartnerByLand?: Record<string, Map<number, number>>
      secondarySortByStudio?: Map<number, number>
      secondarySortByPartner?: Map<number, number>
      secondarySortByPartnerByLand?: Record<string, Map<number, number>>
      partnerNumberByStudio?: Map<number, number | null>
    } = {}
  ): MetricDataset => {
    const meta = METRIC_META.find((item) => item.key === key)!
    return {
      key,
      label: meta.label,
      valueKind: meta.valueKind,
      detailColumnLabel: meta.detailColumnLabel,
      studio: buildScopedRows(studioMetric, {
        allowNonZero: opts.allowNonZero,
        includeAll: opts.includeAll,
        detailByStudio: opts.detailByStudio,
        displayByStudio: opts.displayByStudio,
        sortValueByStudio: opts.sortValueByStudio,
        secondarySortByStudio: opts.secondarySortByStudio,
        partnerNumberByStudio: opts.partnerNumberByStudio,
      }),
      franchisee: buildFranchiseeScopedRows(partnerMetric, landPartnerMetric, {
        allowNonZero: opts.allowNonZero,
        includeAll: opts.includeAll,
        detailByPartner: opts.detailByPartner,
        detailByPartnerByLand: opts.detailByPartnerByLand,
        displayByPartner: opts.displayByPartner,
        displayByPartnerByLand: opts.displayByPartnerByLand,
        sortValueByPartner: opts.sortValueByPartner,
        sortValueByPartnerByLand: opts.sortValueByPartnerByLand,
        secondarySortByPartner: opts.secondarySortByPartner,
        secondarySortByPartnerByLand: opts.secondarySortByPartnerByLand,
      }),
    }
  }

  const revenueByStudio = new Map<number, number>()
  const revenueByPartner = new Map<number, number>()
  const revenueByPartnerByLand: Record<string, Map<number, number>> = {}
  const maxDailyRevenueByStudio = new Map<number, number>()
  const maxDailyRevenueDateByStudio = new Map<number, string>()

  for (const row of normalizedRows) {
    revenueByStudio.set(row.studio_internal_id, (revenueByStudio.get(row.studio_internal_id) || 0) + row.gross_revenue_rub)
    if (row.owner_partner_number != null) {
      revenueByPartner.set(row.owner_partner_number, (revenueByPartner.get(row.owner_partner_number) || 0) + row.gross_revenue_rub)
      const landName = studioMeta.get(row.studio_internal_id)?.land_group_name || 'Без земли'
      const byPartner = revenueByPartnerByLand[landName] || new Map<number, number>()
      byPartner.set(row.owner_partner_number, (byPartner.get(row.owner_partner_number) || 0) + row.gross_revenue_rub)
      revenueByPartnerByLand[landName] = byPartner
    }

    const currentMax = maxDailyRevenueByStudio.get(row.studio_internal_id) || 0
    if (row.max_daily_revenue_rub > currentMax) {
      maxDailyRevenueByStudio.set(row.studio_internal_id, row.max_daily_revenue_rub)
      if (row.max_daily_revenue_date) {
        maxDailyRevenueDateByStudio.set(row.studio_internal_id, formatDisplayDate(row.max_daily_revenue_date))
      }
    }
  }

  // Пик дневной выручки франчайзи (сеть и разрез по землям) приходит уже посчитанным из БД.
  const maxDailyRevenueByPartner = new Map<number, number>()
  const maxDailyRevenueDateByPartner = new Map<number, string>()
  const maxDailyRevenueByPartnerByLand: Record<string, Map<number, number>> = {}
  const maxDailyRevenueDateByPartnerByLand: Record<string, Map<number, string>> = {}

  for (const row of partnerMaxDailyRows) {
    if (!Number.isFinite(row.owner_partner_number)) continue

    if (row.scope_kind === 'network') {
      maxDailyRevenueByPartner.set(row.owner_partner_number, row.max_daily_revenue_rub)
      if (row.max_daily_revenue_date) {
        maxDailyRevenueDateByPartner.set(row.owner_partner_number, formatDisplayDate(row.max_daily_revenue_date))
      }
      continue
    }

    const landName = row.land_group_name || 'Без земли'
    const valueMap = maxDailyRevenueByPartnerByLand[landName] || new Map<number, number>()
    valueMap.set(row.owner_partner_number, row.max_daily_revenue_rub)
    maxDailyRevenueByPartnerByLand[landName] = valueMap

    if (row.max_daily_revenue_date) {
      const dateMap = maxDailyRevenueDateByPartnerByLand[landName] || new Map<number, string>()
      dateMap.set(row.owner_partner_number, formatDisplayDate(row.max_daily_revenue_date))
      maxDailyRevenueDateByPartnerByLand[landName] = dateMap
    }
  }

  const clientsByStudio = new Map<number, number>()
  const clientGrowthByStudio = new Map<number, number>()
  const clientsByPartner = new Map<number, number>()
  const clientGrowthByPartner = new Map<number, number>()
  const clientsByPartnerByLand: Record<string, Map<number, number>> = {}
  const clientGrowthByPartnerByLand: Record<string, Map<number, number>> = {}
  const clientMetricRankByStudio = new Map<number, number>()

  for (const row of normalizedRows) {
    const clientRank =
      (row.clients_at_end != null ? 1_000_000_000 : 0) +
      (row.clients_growth != null ? 100_000_000 : 0) +
      new Date(row.last_metric_date || row.max_daily_revenue_date || '1970-01-01').getTime()

    const currentRank = clientMetricRankByStudio.get(row.studio_internal_id) ?? Number.NEGATIVE_INFINITY
    if (clientRank < currentRank) continue

    if (row.clients_at_end != null) {
      clientsByStudio.set(row.studio_internal_id, row.clients_at_end)
    }

    if (row.clients_growth != null) {
      clientGrowthByStudio.set(row.studio_internal_id, row.clients_growth)
    } else if (params.rangeMode === 'yesterday' && row.clients_at_end != null) {
      clientGrowthByStudio.set(row.studio_internal_id, 0)
    }

    clientMetricRankByStudio.set(row.studio_internal_id, clientRank)
  }

  for (const [studioId, clientsAtEnd] of clientsByStudio.entries()) {
    const partnerNumber = partnerAtRangeEndByStudio.get(studioId)
    if (partnerNumber == null) continue
    const landName = studioMeta.get(studioId)?.land_group_name || 'Без земли'
    clientsByPartner.set(partnerNumber, (clientsByPartner.get(partnerNumber) || 0) + clientsAtEnd)
    clientGrowthByPartner.set(
      partnerNumber,
      (clientGrowthByPartner.get(partnerNumber) || 0) + (clientGrowthByStudio.get(studioId) || 0)
    )
    const landClients = clientsByPartnerByLand[landName] || new Map<number, number>()
    landClients.set(partnerNumber, (landClients.get(partnerNumber) || 0) + clientsAtEnd)
    clientsByPartnerByLand[landName] = landClients
    const landGrowth = clientGrowthByPartnerByLand[landName] || new Map<number, number>()
    landGrowth.set(partnerNumber, (landGrowth.get(partnerNumber) || 0) + (clientGrowthByStudio.get(studioId) || 0))
    clientGrowthByPartnerByLand[landName] = landGrowth
  }

  const activeSubsByStudio = new Map<number, number>()
  const activeSubsByPartner = new Map<number, number>()
  const activeSubsByPartnerByLand: Record<string, Map<number, number>> = {}
  const subscriptionSalesByStudio = new Map<number, number>()
  const subscriptionSalesByPartner = new Map<number, number>()
  const subscriptionSalesByPartnerByLand: Record<string, Map<number, number>> = {}
  const subscriptionSalesByStudioOwner = new Map<string, number>()
  for (const row of subscriptionSalesRows) {
    subscriptionSalesByStudioOwner.set(
      `${row.studio_internal_id}:${row.owner_partner_number}`,
      row.subscription_sales_count
    )
  }
  const getSubscriptionSalesCount = (row: ManagementRatingRow): number => {
    if (row.owner_partner_number == null) return 0
    return subscriptionSalesByStudioOwner.get(`${row.studio_internal_id}:${row.owner_partner_number}`) ?? 0
  }
  const leadsByStudio = new Map<number, number>()
  const marketingSpendByStudio = new Map<number, number>()
  const admissionsByStudio = new Map<number, number>()
  const newVisitorsByStudio = new Map<number, number>()
  const newVisitorsByPartner = new Map<number, number>()
  const conversionSalesCountByStudio = new Map<number, number>()
  const newSalesAmountByStudio = new Map<number, number>()
  const newSalesCountByStudio = new Map<number, number>()
  const salesConversionByStudio = new Map<number, number>()
  const salesConversionByPartner = new Map<number, number>()
  const averageTicketByStudio = new Map<number, number>()
  const averageTicketSortByStudio = new Map<number, number>()
  const averageTicketDisplayByStudio = new Map<number, string>()
  const averageTicketByPartner = new Map<number, number>()
  const averageTicketSortByPartner = new Map<number, number>()
  const averageTicketDisplayByPartner = new Map<number, string>()
  const averageTicketByPartnerByLand: Record<string, Map<number, number>> = {}
  const averageTicketSortByPartnerByLand: Record<string, Map<number, number>> = {}
  const averageTicketDisplayByPartnerByLand: Record<string, Map<number, string>> = {}
  const costPerLeadByStudio = new Map<number, number>()
  const costPerLeadByPartner = new Map<number, number>()
  const costPerLeadSortByStudio = new Map<number, number>()
  const costPerLeadSortByPartner = new Map<number, number>()
  const costPerLeadByPartnerByLand: Record<string, Map<number, number>> = {}
  const costPerLeadSortByPartnerByLand: Record<string, Map<number, number>> = {}
  const romiByStudio = new Map<number, number>()
  const romiByPartner = new Map<number, number>()
  const romiByPartnerByLand: Record<string, Map<number, number>> = {}
  const romiDisplayByStudio = new Map<number, string>()
  const romiDisplayByPartner = new Map<number, string>()
  const romiDisplayByPartnerByLand: Record<string, Map<number, string>> = {}

  const conversionSalesCountByPartner = new Map<number, number>()
  const newSalesAmountByPartner = new Map<number, number>()
  const newSalesCountByPartner = new Map<number, number>()
  const marketingSpendByPartner = new Map<number, number>()
  const mixedNewRevenueByStudio = new Map<number, number>()
  const mixedNewRevenueByPartner = new Map<number, number>()
  const leadsByPartnerByLand = new Map<string, Map<number, number>>()
  const marketingSpendByPartnerByLand = new Map<string, Map<number, number>>()
  const newVisitorsByPartnerByLand = new Map<string, Map<number, number>>()
  const conversionSalesCountByPartnerByLand = new Map<string, Map<number, number>>()
  const newSalesAmountByPartnerByLand = new Map<string, Map<number, number>>()
  const newSalesCountByPartnerByLand = new Map<string, Map<number, number>>()
  const mixedNewRevenueByPartnerByLand = new Map<string, Map<number, number>>()
  const salesConversionByPartnerByLand: Record<string, Map<number, number>> = {}

  for (const row of normalizedRows) {
    leadsByStudio.set(row.studio_internal_id, (leadsByStudio.get(row.studio_internal_id) || 0) + row.leads_count)
    marketingSpendByStudio.set(
      row.studio_internal_id,
      (marketingSpendByStudio.get(row.studio_internal_id) || 0) + row.marketing_expenses_amount_rub
    )
    admissionsByStudio.set(row.studio_internal_id, (admissionsByStudio.get(row.studio_internal_id) || 0) + row.admissions_count)
    newVisitorsByStudio.set(
      row.studio_internal_id,
      (newVisitorsByStudio.get(row.studio_internal_id) || 0) + row.new_visitors_count
    )
    conversionSalesCountByStudio.set(
      row.studio_internal_id,
      (conversionSalesCountByStudio.get(row.studio_internal_id) || 0) + row.new_sales_count + row.new_subscriptions_count
    )
    newSalesAmountByStudio.set(row.studio_internal_id, (newSalesAmountByStudio.get(row.studio_internal_id) || 0) + row.new_sales_amount_rub)
    newSalesCountByStudio.set(row.studio_internal_id, (newSalesCountByStudio.get(row.studio_internal_id) || 0) + row.new_sales_count)
    mixedNewRevenueByStudio.set(
      row.studio_internal_id,
      (mixedNewRevenueByStudio.get(row.studio_internal_id) || 0) + row.new_sales_amount_rub + row.new_subscriptions_amount_rub
    )
    activeSubsByStudio.set(row.studio_internal_id, (activeSubsByStudio.get(row.studio_internal_id) || 0) + (row.active_subscribers_last || 0))
    const subscriptionSalesCount = getSubscriptionSalesCount(row)
    subscriptionSalesByStudio.set(
      row.studio_internal_id,
      (subscriptionSalesByStudio.get(row.studio_internal_id) || 0) + subscriptionSalesCount
    )

    if (row.owner_partner_number != null) {
      const landName = studioMeta.get(row.studio_internal_id)?.land_group_name || 'Без земли'
      marketingSpendByPartner.set(
        row.owner_partner_number,
        (marketingSpendByPartner.get(row.owner_partner_number) || 0) + row.marketing_expenses_amount_rub
      )
      newVisitorsByPartner.set(
        row.owner_partner_number,
        (newVisitorsByPartner.get(row.owner_partner_number) || 0) + row.new_visitors_count
      )
      conversionSalesCountByPartner.set(
        row.owner_partner_number,
        (conversionSalesCountByPartner.get(row.owner_partner_number) || 0) + row.new_sales_count + row.new_subscriptions_count
      )
      newSalesAmountByPartner.set(row.owner_partner_number, (newSalesAmountByPartner.get(row.owner_partner_number) || 0) + row.new_sales_amount_rub)
      newSalesCountByPartner.set(row.owner_partner_number, (newSalesCountByPartner.get(row.owner_partner_number) || 0) + row.new_sales_count)
      mixedNewRevenueByPartner.set(
        row.owner_partner_number,
        (mixedNewRevenueByPartner.get(row.owner_partner_number) || 0) + row.new_sales_amount_rub + row.new_subscriptions_amount_rub
      )
      activeSubsByPartner.set(row.owner_partner_number, (activeSubsByPartner.get(row.owner_partner_number) || 0) + (row.active_subscribers_last || 0))
      subscriptionSalesByPartner.set(
        row.owner_partner_number,
        (subscriptionSalesByPartner.get(row.owner_partner_number) || 0) + subscriptionSalesCount
      )
      const subscriptionSalesLand = subscriptionSalesByPartnerByLand[landName] || new Map<number, number>()
      subscriptionSalesLand.set(
        row.owner_partner_number,
        (subscriptionSalesLand.get(row.owner_partner_number) || 0) + subscriptionSalesCount
      )
      subscriptionSalesByPartnerByLand[landName] = subscriptionSalesLand
      const activeSubsLand = activeSubsByPartnerByLand[landName] || new Map<number, number>()
      activeSubsLand.set(
        row.owner_partner_number,
        (activeSubsLand.get(row.owner_partner_number) || 0) + (row.active_subscribers_last || 0)
      )
      activeSubsByPartnerByLand[landName] = activeSubsLand

      const leadsLand = leadsByPartnerByLand.get(landName) || new Map<number, number>()
      leadsLand.set(row.owner_partner_number, (leadsLand.get(row.owner_partner_number) || 0) + row.leads_count)
      leadsByPartnerByLand.set(landName, leadsLand)

      const marketingLand = marketingSpendByPartnerByLand.get(landName) || new Map<number, number>()
      marketingLand.set(
        row.owner_partner_number,
        (marketingLand.get(row.owner_partner_number) || 0) + row.marketing_expenses_amount_rub
      )
      marketingSpendByPartnerByLand.set(landName, marketingLand)

      const visitorsLand = newVisitorsByPartnerByLand.get(landName) || new Map<number, number>()
      visitorsLand.set(
        row.owner_partner_number,
        (visitorsLand.get(row.owner_partner_number) || 0) + row.new_visitors_count
      )
      newVisitorsByPartnerByLand.set(landName, visitorsLand)

      const conversionLand = conversionSalesCountByPartnerByLand.get(landName) || new Map<number, number>()
      conversionLand.set(
        row.owner_partner_number,
        (conversionLand.get(row.owner_partner_number) || 0) + row.new_sales_count + row.new_subscriptions_count
      )
      conversionSalesCountByPartnerByLand.set(landName, conversionLand)

      const salesAmountLand = newSalesAmountByPartnerByLand.get(landName) || new Map<number, number>()
      salesAmountLand.set(
        row.owner_partner_number,
        (salesAmountLand.get(row.owner_partner_number) || 0) + row.new_sales_amount_rub
      )
      newSalesAmountByPartnerByLand.set(landName, salesAmountLand)

      const salesCountLand = newSalesCountByPartnerByLand.get(landName) || new Map<number, number>()
      salesCountLand.set(
        row.owner_partner_number,
        (salesCountLand.get(row.owner_partner_number) || 0) + row.new_sales_count
      )
      newSalesCountByPartnerByLand.set(landName, salesCountLand)

      const mixedRevenueLand = mixedNewRevenueByPartnerByLand.get(landName) || new Map<number, number>()
      mixedRevenueLand.set(
        row.owner_partner_number,
        (mixedRevenueLand.get(row.owner_partner_number) || 0) + row.new_sales_amount_rub + row.new_subscriptions_amount_rub
      )
      mixedNewRevenueByPartnerByLand.set(landName, mixedRevenueLand)
    }
  }

  for (const [studioId, newVisitors] of newVisitorsByStudio.entries()) {
    if (newVisitors > 0) {
      salesConversionByStudio.set(studioId, ((conversionSalesCountByStudio.get(studioId) || 0) / newVisitors) * 100)
    }
  }

  for (const [partnerNumber, newVisitors] of newVisitorsByPartner.entries()) {
    if (newVisitors > 0) {
      salesConversionByPartner.set(
        partnerNumber,
        ((conversionSalesCountByPartner.get(partnerNumber) || 0) / newVisitors) * 100
      )
    }
  }

  for (const [landName, visitorsByPartner] of newVisitorsByPartnerByLand.entries()) {
    const conversionMap = new Map<number, number>()
    for (const [partnerNumber, newVisitors] of visitorsByPartner.entries()) {
      if (newVisitors > 0) {
        conversionMap.set(
          partnerNumber,
          ((conversionSalesCountByPartnerByLand.get(landName)?.get(partnerNumber) || 0) / newVisitors) * 100
        )
      }
    }
    salesConversionByPartnerByLand[landName] = conversionMap
  }

  for (const [studioId, salesCount] of newSalesCountByStudio.entries()) {
    if (salesCount > 0) {
      const value = (newSalesAmountByStudio.get(studioId) || 0) / salesCount
      averageTicketByStudio.set(studioId, value)
      averageTicketSortByStudio.set(studioId, value)
    } else {
      averageTicketByStudio.set(studioId, 0)
      averageTicketSortByStudio.set(studioId, -1)
      averageTicketDisplayByStudio.set(studioId, '—')
    }
  }

  for (const [partnerNumber, salesCount] of newSalesCountByPartner.entries()) {
    if (salesCount > 0) {
      const value = (newSalesAmountByPartner.get(partnerNumber) || 0) / salesCount
      averageTicketByPartner.set(partnerNumber, value)
      averageTicketSortByPartner.set(partnerNumber, value)
    } else {
      averageTicketByPartner.set(partnerNumber, 0)
      averageTicketSortByPartner.set(partnerNumber, -1)
      averageTicketDisplayByPartner.set(partnerNumber, '—')
    }
  }

  for (const [landName, salesCountByPartner] of newSalesCountByPartnerByLand.entries()) {
    const valueMap = new Map<number, number>()
    const sortMap = new Map<number, number>()
    const displayMap = new Map<number, string>()
    for (const [partnerNumber, salesCount] of salesCountByPartner.entries()) {
      if (salesCount > 0) {
        const value = (newSalesAmountByPartnerByLand.get(landName)?.get(partnerNumber) || 0) / salesCount
        valueMap.set(partnerNumber, value)
        sortMap.set(partnerNumber, value)
      } else {
        valueMap.set(partnerNumber, 0)
        sortMap.set(partnerNumber, -1)
        displayMap.set(partnerNumber, '—')
      }
    }
    averageTicketByPartnerByLand[landName] = valueMap
    averageTicketSortByPartnerByLand[landName] = sortMap
    averageTicketDisplayByPartnerByLand[landName] = displayMap
  }

  for (const [studioId, leads] of leadsByStudio.entries()) {
    const marketingSpend = marketingSpendByStudio.get(studioId) || 0
    if (leads > 0 && marketingSpend > 0) {
      const value = marketingSpend / leads
      costPerLeadByStudio.set(studioId, value)
      costPerLeadSortByStudio.set(studioId, -value)
    }
  }

  for (const [partnerNumber, leads] of new Map(
    Array.from(leadsByStudio.entries()).reduce((acc, [studioId, leads]) => {
      const partnerNumber = partnerAtRangeEndByStudio.get(studioId)
      if (partnerNumber == null) return acc
      acc.set(partnerNumber, (acc.get(partnerNumber) || 0) + leads)
      return acc
    }, new Map<number, number>())
  ).entries()) {
    const marketingSpend = marketingSpendByPartner.get(partnerNumber) || 0
    if (leads > 0 && marketingSpend > 0) {
      const value = marketingSpend / leads
      costPerLeadByPartner.set(partnerNumber, value)
      costPerLeadSortByPartner.set(partnerNumber, -value)
    }
  }

  for (const [landName, leadsMap] of leadsByPartnerByLand.entries()) {
    const valueMap = new Map<number, number>()
    const sortMap = new Map<number, number>()
    for (const [partnerNumber, leads] of leadsMap.entries()) {
      const marketingSpend = marketingSpendByPartnerByLand.get(landName)?.get(partnerNumber) || 0
      if (leads > 0 && marketingSpend > 0) {
        const value = marketingSpend / leads
        valueMap.set(partnerNumber, value)
        sortMap.set(partnerNumber, -value)
      }
    }
    costPerLeadByPartnerByLand[landName] = valueMap
    costPerLeadSortByPartnerByLand[landName] = sortMap
  }

  for (const [studioId, mixedNewRevenue] of mixedNewRevenueByStudio.entries()) {
    const marketingSpend = marketingSpendByStudio.get(studioId) || 0
    if (marketingSpend > 0) {
      const value = mixedNewRevenue / marketingSpend
      romiByStudio.set(studioId, value)
      romiDisplayByStudio.set(studioId, `${value.toFixed(2)}x`)
    }
  }

  for (const [partnerNumber, mixedNewRevenue] of mixedNewRevenueByPartner.entries()) {
    const marketingSpend = marketingSpendByPartner.get(partnerNumber) || 0
    if (marketingSpend > 0) {
      const value = mixedNewRevenue / marketingSpend
      romiByPartner.set(partnerNumber, value)
      romiDisplayByPartner.set(partnerNumber, `${value.toFixed(2)}x`)
    }
  }

  for (const [landName, mixedRevenueMap] of mixedNewRevenueByPartnerByLand.entries()) {
    const valueMap = new Map<number, number>()
    const displayMap = new Map<number, string>()
    for (const [partnerNumber, mixedNewRevenue] of mixedRevenueMap.entries()) {
      const marketingSpend = marketingSpendByPartnerByLand.get(landName)?.get(partnerNumber) || 0
      if (marketingSpend > 0) {
        const value = mixedNewRevenue / marketingSpend
        valueMap.set(partnerNumber, value)
        displayMap.set(partnerNumber, `${value.toFixed(2)}x`)
      }
    }
    romiByPartnerByLand[landName] = valueMap
    romiDisplayByPartnerByLand[landName] = displayMap
  }

  const leadsPerStudioPartner = buildPartnerEquivalentMetric(normalizedRows, (row) => row.leads_count)
  const leadsPerStudioPartnerByLand = buildPartnerEquivalentMetricByLand(normalizedRows, studioMeta, (row) => row.leads_count)
  const visitsPerStudioPartner = buildPartnerEquivalentMetric(normalizedRows, (row) => row.admissions_count)
  const visitsPerStudioPartnerByLand = buildPartnerEquivalentMetricByLand(normalizedRows, studioMeta, (row) => row.admissions_count)

  const classicIndexByStudio = new Map<number, number>()
  const classicSortByStudio = new Map<number, number>()
  const classicDetailByStudio = new Map<number, string>()
  const classicClassesSortByStudio = new Map<number, number>()
  const classicDisplayByStudio = new Map<number, string>()
  const classicPartnerNumberByStudio = new Map<number, number | null>()
  const classicIndexByPartner = new Map<number, number>()
  const classicSortByPartner = new Map<number, number>()
  const classicDetailByPartner = new Map<number, string>()
  const classicClassesSortByPartner = new Map<number, number>()
  const classicDisplayByPartner = new Map<number, string>()
  const classicIndexByPartnerByLand: Record<string, Map<number, number>> = {}
  const classicSortByPartnerByLand: Record<string, Map<number, number>> = {}
  const classicDetailByPartnerByLand: Record<string, Map<number, string>> = {}
  const classicClassesSortByPartnerByLand: Record<string, Map<number, number>> = {}
  const classicDisplayByPartnerByLand: Record<string, Map<number, string>> = {}
  const classicPartnerAggregates = new Map<number, {
    totalClassicClasses: number
    visitsWithCapacity: number
    seatCapacityWeightedClasses: number
  }>()
  const classicPartnerAggregatesByLand = new Map<string, Map<number, {
    totalClassicClasses: number
    visitsWithCapacity: number
    seatCapacityWeightedClasses: number
  }>>()

  for (const row of classicRatingRows) {
    if (!eligibleStudioIdSet.has(row.studio_internal_id)) continue

    studioMeta.set(row.studio_internal_id, {
      studio_number: String(row.studio_number),
      city_name: row.city_name || studioMeta.get(row.studio_internal_id)?.city_name || 'Город не указан',
      country: row.country || studioMeta.get(row.studio_internal_id)?.country || null,
      address: studioMeta.get(row.studio_internal_id)?.address || 'Адрес не указан',
      land_group_name: row.land_group_name || studioMeta.get(row.studio_internal_id)?.land_group_name || 'Без земли',
      population_group_name: row.population_group_name || studioMeta.get(row.studio_internal_id)?.population_group_name || 'Без группы',
      partner_number: row.owner_partner_number ?? studioMeta.get(row.studio_internal_id)?.partner_number ?? null,
      partner_label: row.owner_partner_number != null
        ? normalizeOwnerLabel(row.owner_partner_number, row.owner_persons_fio, params.partnerLabelMap)
        : (studioMeta.get(row.studio_internal_id)?.partner_label || 'Франчайзи не указан'),
    })

    const studioPercentValue = row.classic_group_index == null ? null : row.classic_group_index * 100
    classicIndexByStudio.set(row.studio_internal_id, studioPercentValue ?? 0)
    classicSortByStudio.set(row.studio_internal_id, studioPercentValue ?? -1)
    classicDetailByStudio.set(row.studio_internal_id, formatNumber(row.classic_classes_count))
    classicClassesSortByStudio.set(row.studio_internal_id, row.classic_classes_count)
    classicPartnerNumberByStudio.set(row.studio_internal_id, row.owner_partner_number)
    if (row.classic_group_index == null) classicDisplayByStudio.set(row.studio_internal_id, '—')

    if (row.owner_partner_number != null) {
      const landName = row.land_group_name || studioMeta.get(row.studio_internal_id)?.land_group_name || 'Без земли'
      const aggregate = classicPartnerAggregates.get(row.owner_partner_number) || {
        totalClassicClasses: 0,
        visitsWithCapacity: 0,
        seatCapacityWeightedClasses: 0,
      }
      aggregate.totalClassicClasses += row.classic_classes_count
      if (row.average_capacity != null) {
        aggregate.visitsWithCapacity += row.classic_visits_count
        aggregate.seatCapacityWeightedClasses += row.classic_classes_count * row.average_capacity
      }
      classicPartnerAggregates.set(row.owner_partner_number, aggregate)

      const byLand = classicPartnerAggregatesByLand.get(landName) || new Map<number, {
        totalClassicClasses: number
        visitsWithCapacity: number
        seatCapacityWeightedClasses: number
      }>()
      const landAggregate = byLand.get(row.owner_partner_number) || {
        totalClassicClasses: 0,
        visitsWithCapacity: 0,
        seatCapacityWeightedClasses: 0,
      }
      landAggregate.totalClassicClasses += row.classic_classes_count
      if (row.average_capacity != null) {
        landAggregate.visitsWithCapacity += row.classic_visits_count
        landAggregate.seatCapacityWeightedClasses += row.classic_classes_count * row.average_capacity
      }
      byLand.set(row.owner_partner_number, landAggregate)
      classicPartnerAggregatesByLand.set(landName, byLand)
    }
  }

  for (const [partnerNumber, aggregate] of classicPartnerAggregates.entries()) {
    const ratio = aggregate.seatCapacityWeightedClasses > 0
      ? aggregate.visitsWithCapacity / aggregate.seatCapacityWeightedClasses
      : null
    const percentValue = ratio == null ? null : ratio * 100
    classicIndexByPartner.set(partnerNumber, percentValue ?? 0)
    classicSortByPartner.set(partnerNumber, percentValue ?? -1)
    classicDetailByPartner.set(partnerNumber, formatNumber(aggregate.totalClassicClasses))
    classicClassesSortByPartner.set(partnerNumber, aggregate.totalClassicClasses)
    if (ratio == null) classicDisplayByPartner.set(partnerNumber, '—')
  }

  for (const [landName, byPartner] of classicPartnerAggregatesByLand.entries()) {
    const valueMap = new Map<number, number>()
    const sortMap = new Map<number, number>()
    const detailMap = new Map<number, string>()
    const secondaryMap = new Map<number, number>()
    const displayMap = new Map<number, string>()
    for (const [partnerNumber, aggregate] of byPartner.entries()) {
      const ratio = aggregate.seatCapacityWeightedClasses > 0
        ? aggregate.visitsWithCapacity / aggregate.seatCapacityWeightedClasses
        : null
      const percentValue = ratio == null ? null : ratio * 100
      valueMap.set(partnerNumber, percentValue ?? 0)
      sortMap.set(partnerNumber, percentValue ?? -1)
      detailMap.set(partnerNumber, formatNumber(aggregate.totalClassicClasses))
      secondaryMap.set(partnerNumber, aggregate.totalClassicClasses)
      if (ratio == null) displayMap.set(partnerNumber, '—')
    }
    classicIndexByPartnerByLand[landName] = valueMap
    classicSortByPartnerByLand[landName] = sortMap
    classicDetailByPartnerByLand[landName] = detailMap
    classicClassesSortByPartnerByLand[landName] = secondaryMap
    classicDisplayByPartnerByLand[landName] = displayMap
  }

  const trainingQualityByStudio = new Map<number, number>()
  const trainingQualityByPartner = new Map<number, number>()
  const trainingQualityByPartnerByLand: Record<string, Map<number, number>> = {}
  try {
    const studioTotals = new Map<number, { totalScore: number; maxScore: number }>()
    const partnerTotals = new Map<number, { totalScore: number; maxScore: number }>()
    const partnerTotalsByLand = new Map<string, Map<number, { totalScore: number; maxScore: number }>>()
    const ownerLabelsByStudio = new Map<number, Array<{ label: string; ownerMoment: string }>>()
    const latestOwnerByStudio = new Map<number, { partnerNumber: number; ownerMoment: string; ownerLabel: string }>()

    for (const row of qualityRows) {
      if (!eligibleStudioIdSet.has(row.studio_internal_id)) continue
      if (!Number.isFinite(row.total_score) || !Number.isFinite(row.max_score) || row.max_score <= 0 || row.owner_partner_number <= 0) continue

      const studioAggregate = studioTotals.get(row.studio_internal_id) || { totalScore: 0, maxScore: 0 }
      studioAggregate.totalScore += row.total_score
      studioAggregate.maxScore += row.max_score
      studioTotals.set(row.studio_internal_id, studioAggregate)

      const partnerAggregate = partnerTotals.get(row.owner_partner_number) || { totalScore: 0, maxScore: 0 }
      partnerAggregate.totalScore += row.total_score
      partnerAggregate.maxScore += row.max_score
      partnerTotals.set(row.owner_partner_number, partnerAggregate)

      const landName = row.land_group_name || studioMeta.get(row.studio_internal_id)?.land_group_name || 'Без земли'
      const byLand = partnerTotalsByLand.get(landName) || new Map<number, { totalScore: number; maxScore: number }>()
      const landPartnerAggregate = byLand.get(row.owner_partner_number) || { totalScore: 0, maxScore: 0 }
      landPartnerAggregate.totalScore += row.total_score
      landPartnerAggregate.maxScore += row.max_score
      byLand.set(row.owner_partner_number, landPartnerAggregate)
      partnerTotalsByLand.set(landName, byLand)

      const ownerMoment = row.last_training_at || row.first_training_at || ''
      const ownerLabel = normalizeOwnerLabel(row.owner_partner_number, row.owner_persons_fio, params.partnerLabelMap)
      const labels = ownerLabelsByStudio.get(row.studio_internal_id) || []
      labels.push({ label: ownerLabel, ownerMoment })
      ownerLabelsByStudio.set(row.studio_internal_id, labels)

      const current = latestOwnerByStudio.get(row.studio_internal_id)
      if (!current || ownerMoment > current.ownerMoment) {
        latestOwnerByStudio.set(row.studio_internal_id, {
          partnerNumber: row.owner_partner_number,
          ownerMoment,
          ownerLabel,
        })
      }
    }

    for (const [studioId, totals] of studioTotals.entries()) {
      if (totals.maxScore > 0) trainingQualityByStudio.set(studioId, (totals.totalScore / totals.maxScore) * 100)
    }
    for (const [partnerNumber, totals] of partnerTotals.entries()) {
      if (totals.maxScore > 0) trainingQualityByPartner.set(partnerNumber, (totals.totalScore / totals.maxScore) * 100)
    }
    for (const [landName, byPartner] of partnerTotalsByLand.entries()) {
      const valueMap = new Map<number, number>()
      for (const [partnerNumber, totals] of byPartner.entries()) {
        if (totals.maxScore > 0) valueMap.set(partnerNumber, (totals.totalScore / totals.maxScore) * 100)
      }
      trainingQualityByPartnerByLand[landName] = valueMap
    }
    for (const [studioId, labels] of ownerLabelsByStudio.entries()) {
      if (ownerRangeRawLabelByStudio.has(studioId)) continue
      const currentLabel = latestOwnerByStudio.get(studioId)?.ownerLabel || null
      const formatted = formatOwnerRangeLabel(
        labels
          .sort((a, b) => a.ownerMoment.localeCompare(b.ownerMoment))
          .map((item) => item.label)
          .join(' - '),
        currentLabel
      )
      const meta = studioMeta.get(studioId)
      if (meta) meta.partner_label = formatted
    }
    for (const [studioId, latestOwner] of latestOwnerByStudio.entries()) {
      if (!partnerAtRangeEndByStudio.has(studioId)) {
        partnerAtRangeEndByStudio.set(studioId, latestOwner.partnerNumber)
      }
    }
  } catch (error) {
    console.warn('Unified ratings: trainer quality post-processing failed, quality metric will be empty', error)
  }

  return {
    revenue: dataset('revenue', revenueByStudio, revenueByPartner, revenueByPartnerByLand),
    max_daily_revenue: dataset('max_daily_revenue', maxDailyRevenueByStudio, maxDailyRevenueByPartner, maxDailyRevenueByPartnerByLand, {
      detailByStudio: maxDailyRevenueDateByStudio,
      detailByPartner: maxDailyRevenueDateByPartner,
      detailByPartnerByLand: maxDailyRevenueDateByPartnerByLand,
    }),
    clients: dataset('clients', clientsByStudio, clientsByPartner, clientsByPartnerByLand, {
      partnerNumberByStudio: partnerAtRangeEndByStudio,
    }),
    client_growth: dataset('client_growth', clientGrowthByStudio, clientGrowthByPartner, clientGrowthByPartnerByLand, {
      includeAll: true,
      partnerNumberByStudio: partnerAtRangeEndByStudio,
    }),
    sales_conversion: dataset('sales_conversion', salesConversionByStudio, salesConversionByPartner, salesConversionByPartnerByLand, {
      partnerNumberByStudio: partnerAtRangeEndByStudio,
    }),
    active_subscriptions: dataset('active_subscriptions', activeSubsByStudio, activeSubsByPartner, activeSubsByPartnerByLand, {
      partnerNumberByStudio: partnerAtRangeEndByStudio,
    }),
    new_subscriptions: dataset('new_subscriptions', subscriptionSalesByStudio, subscriptionSalesByPartner, subscriptionSalesByPartnerByLand, {
      partnerNumberByStudio: partnerAtRangeEndByStudio,
    }),
    leads_per_studio: dataset('leads_per_studio', leadsByStudio, leadsPerStudioPartner.metricByPartner, leadsPerStudioPartnerByLand.metricByLand, {
      displayByPartner: leadsPerStudioPartner.displayByPartner,
      displayByPartnerByLand: leadsPerStudioPartnerByLand.displayByLand,
      sortValueByPartner: leadsPerStudioPartner.sortByPartner,
      sortValueByPartnerByLand: leadsPerStudioPartnerByLand.sortByLand,
      partnerNumberByStudio: partnerAtRangeEndByStudio,
    }),
    cost_per_lead: dataset('cost_per_lead', costPerLeadByStudio, costPerLeadByPartner, costPerLeadByPartnerByLand, {
      sortValueByStudio: costPerLeadSortByStudio,
      sortValueByPartner: costPerLeadSortByPartner,
      sortValueByPartnerByLand: costPerLeadSortByPartnerByLand,
      partnerNumberByStudio: partnerAtRangeEndByStudio,
    }),
    romi: dataset('romi', romiByStudio, romiByPartner, romiByPartnerByLand, {
      displayByStudio: romiDisplayByStudio,
      displayByPartner: romiDisplayByPartner,
      displayByPartnerByLand: romiDisplayByPartnerByLand,
      partnerNumberByStudio: partnerAtRangeEndByStudio,
    }),
    average_ticket_new_sales: dataset('average_ticket_new_sales', averageTicketByStudio, averageTicketByPartner, averageTicketByPartnerByLand, {
      includeAll: true,
      displayByStudio: averageTicketDisplayByStudio,
      displayByPartner: averageTicketDisplayByPartner,
      displayByPartnerByLand: averageTicketDisplayByPartnerByLand,
      sortValueByStudio: averageTicketSortByStudio,
      sortValueByPartner: averageTicketSortByPartner,
      sortValueByPartnerByLand: averageTicketSortByPartnerByLand,
      secondarySortByStudio: newSalesCountByStudio,
      secondarySortByPartner: newSalesCountByPartner,
      secondarySortByPartnerByLand: Object.fromEntries(
        Object.entries(newSalesCountByPartnerByLand).map(([landName, value]) => [landName, value])
      ) as Record<string, Map<number, number>>,
      partnerNumberByStudio: partnerAtRangeEndByStudio,
    }),
    visits_per_studio: dataset('visits_per_studio', admissionsByStudio, visitsPerStudioPartner.metricByPartner, visitsPerStudioPartnerByLand.metricByLand, {
      displayByPartner: visitsPerStudioPartner.displayByPartner,
      displayByPartnerByLand: visitsPerStudioPartnerByLand.displayByLand,
      sortValueByPartner: visitsPerStudioPartner.sortByPartner,
      sortValueByPartnerByLand: visitsPerStudioPartnerByLand.sortByLand,
      partnerNumberByStudio: partnerAtRangeEndByStudio,
    }),
    classic_group_index: dataset('classic_group_index', classicIndexByStudio, classicIndexByPartner, classicIndexByPartnerByLand, {
      includeAll: true,
      detailByStudio: classicDetailByStudio,
      detailByPartner: classicDetailByPartner,
      detailByPartnerByLand: classicDetailByPartnerByLand,
      displayByStudio: classicDisplayByStudio,
      displayByPartner: classicDisplayByPartner,
      displayByPartnerByLand: classicDisplayByPartnerByLand,
      sortValueByStudio: classicSortByStudio,
      sortValueByPartner: classicSortByPartner,
      sortValueByPartnerByLand: classicSortByPartnerByLand,
      secondarySortByStudio: classicClassesSortByStudio,
      secondarySortByPartner: classicClassesSortByPartner,
      secondarySortByPartnerByLand: classicClassesSortByPartnerByLand,
      partnerNumberByStudio: classicPartnerNumberByStudio,
    }),
    training_quality: dataset('training_quality', trainingQualityByStudio, trainingQualityByPartner, trainingQualityByPartnerByLand, {
      partnerNumberByStudio: partnerAtRangeEndByStudio,
    }),
  }
}

export function findSelfScopeKeys(data: UnifiedRatingsData, studioId: number): SelfScopeKeys {
  const studioRowId = `studio-${studioId}`
  const datasets = Object.values(data)

  const findMatchingKey = (
    selector: (dataset: MetricDataset) => Array<[string, BoardRow[]]>
  ): string | undefined => {
    for (const dataset of datasets) {
      for (const [key, rows] of selector(dataset)) {
        if (rows.some((row) => row.id === studioRowId)) return key
      }
    }
    return undefined
  }

  return {
    land: findMatchingKey((dataset) => Object.entries(dataset.studio.landByKey)),
    size: findMatchingKey((dataset) => Object.entries(dataset.studio.sizeByKey)),
    partner: findMatchingKey((dataset) => Object.entries(dataset.studio.partnerByKey)),
  }
}

function buildEmptyScopedRows(): ScopedRows {
  return { network: [], landOptions: [], landByKey: {}, sizeOptions: [], sizeByKey: {}, partnerOptions: [], partnerByKey: {} }
}

export function buildEmptyDataset(): UnifiedRatingsData {
  const empty = (key: MetricKey): MetricDataset => {
    const meta = METRIC_META.find((item) => item.key === key)!
    return {
      key,
      label: meta.label,
      valueKind: meta.valueKind,
      detailColumnLabel: meta.detailColumnLabel,
      studio: buildEmptyScopedRows(),
      franchisee: { network: [], landOptions: [], landByKey: {} },
    }
  }

  return {
    revenue: empty('revenue'),
    max_daily_revenue: empty('max_daily_revenue'),
    clients: empty('clients'),
    client_growth: empty('client_growth'),
    sales_conversion: empty('sales_conversion'),
    active_subscriptions: empty('active_subscriptions'),
    new_subscriptions: empty('new_subscriptions'),
    leads_per_studio: empty('leads_per_studio'),
    cost_per_lead: empty('cost_per_lead'),
    romi: empty('romi'),
    average_ticket_new_sales: empty('average_ticket_new_sales'),
    visits_per_studio: empty('visits_per_studio'),
    classic_group_index: empty('classic_group_index'),
    training_quality: empty('training_quality'),
  }
}
